import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Star, Spinner, Image as ImageIcon } from "@phosphor-icons/react";
import { uploadPhoto } from "@/lib/uploadPhoto";

const ensureHttps = (u) => (u && !/^https?:\/\//i.test(u) && !u.startsWith("data:") ? `https://${u}` : u);

// Foto de portada: la primera que se ve en la tienda. La galería se conserva intacta.
export default function CoverPhotoField({ value, gallery = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const cover = (value || "").trim();
  const src = cover ? ensureHttps(cover) : null;
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Máx 8 MB"); return; }
    setUploading(true);
    try {
      onChange(await uploadPhoto(file, { folder: "alendrapc/products/cover", maxSide: 1800, quality: 0.88 }));
      toast.success("Portada actualizada");
    } catch (err) {
      toast.error(err?.message || "No se pudo subir la portada");
    } finally { setUploading(false); }
  };
  return (
    <div className="rounded-md border border-[#002FA7]/25 bg-[#002FA7]/[0.03] p-3" data-testid="form-cover">
      <div className="flex items-start gap-4">
        <div className={`w-28 h-28 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0 relative border ${cover ? "border-[#002FA7]/40 bg-white" : "border-dashed border-zinc-300 bg-white"}`} data-testid="form-cover-preview">
          {src ? <img src={src} alt="Portada" className="max-w-full max-h-full object-contain" /> : <span className="text-[10px] text-zinc-400 flex flex-col items-center gap-1 text-center px-2"><ImageIcon size={18} /> sin portada</span>}
          {cover && <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#002FA7] text-white px-1.5 py-px rounded-sm"><Star size={9} weight="fill" /> Portada</span>}
          {uploading && <span className="absolute inset-0 bg-white/70 flex items-center justify-center"><Spinner size={18} className="animate-spin text-[#002FA7]" /></span>}
        </div>
        <div className="flex-1 space-y-2 min-w-0">
          <div className="text-xs font-semibold text-zinc-800 inline-flex items-center gap-1.5"><Star size={13} weight="fill" className="text-[#002FA7]" /> Foto de portada <span className="text-[10px] font-normal text-zinc-500">(opcional)</span></div>
          <p className="text-[11px] text-zinc-500">Es la <b>primera imagen que se ve en la tienda</b> (ideal para tu promocional). Las demás fotos se conservan. Si no hay portada se usa la foto principal.</p>
          <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => upload(e.target.files?.[0])} disabled={uploading}
            className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#002FA7] file:text-white file:rounded file:cursor-pointer file:hover:bg-[#002277]" data-testid="form-cover-upload" />
          <Input type="text" placeholder="…o pega un link de la imagen de portada" value={cover.startsWith("data:") ? "" : cover} onChange={(e) => onChange(e.target.value)} className="h-8 text-xs" data-testid="form-cover-url" />
          <div className="flex items-center gap-3 flex-wrap">
            {cover && <button type="button" onClick={() => onChange("")} className="text-[11px] text-[#E0000F] hover:underline" data-testid="form-cover-clear">Quitar portada</button>}
            {gallery.filter((g) => g && g !== cover).length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] text-zinc-500">Usar de la galería:</span>
                {gallery.filter((g) => g && g !== cover).slice(0, 8).map((g, i) => (
                  <button type="button" key={i} onClick={() => onChange(g)} className="w-9 h-9 rounded border border-zinc-200 overflow-hidden hover:border-[#002FA7] hover:ring-1 hover:ring-[#002FA7]/40 bg-white" title="Usar como portada" data-testid={`form-cover-pick-${i}`}>
                    <img src={ensureHttps(g)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
